/**
 * Echo Hunt -- bullet extraction (P4, §4.1 / §4.4-granularity).
 *
 * ## Source field
 *
 * The corpus is built EXCLUSIVELY from `JournalSegment.publicText` -- the bulleted markdown the user
 * posts publicly -- and NEVER from `longText`/`shortText`. Two reasons (decided 2026-10-19):
 *  1. the user is only comfortable with publicly-shared material being used; and
 *  2. the public posts are far more standardized in form than the raw transcribed long-text.
 *
 * ## publicText format
 *
 * ```
 * # First lucid                      <- level-1 heading: a "group" (one dream) within the night
 *
 * The first lucid is largely forgotten now...   <- prose; context only, never a bullet
 *
 * ### On trip; hotel; store; conversation...    <- level-2+ heading: a section title
 * * The section title is pretty much all I still remember.
 *
 * ### Visual oddities; ask sister if sees it
 * * Next thing I recall is being in my bedroom.
 * * I am near my computer desk, when I turn...
 *   * an indented child bullet           <- belongs to the bullet above, merged into it
 * ```
 *
 *  - `# <group>` lines group the night into dreams. A `publicText` lives on ONE segment, so one
 *    segment is one dream: if a post contains more than one group, only the FIRST is extracted and a
 *    `multipleGroups` warning is reported (surfaced in the Corpus tab).
 *  - `### <title>` (or any `##`..`######`) sets `heading` for the bullets that follow it.
 *  - `* ` / `- ` / `+ ` lines are bullets; any indented bullet is a child of the nearest preceding
 *    top-level bullet and is MERGED into it (§4.4 "nested bullets: treat parent + children as one
 *    unit"), joined with `"; "`.
 *  - Everything else (prose paragraphs, blank lines) is skipped: prose is context, not bullets.
 *
 * ## IDs
 *
 * A bullet's `id` is `entryId/segmentId/<ordinal>` where `ordinal` counts TOP-LEVEL bullets in the
 * segment. It is paired with `sourceTextHash` (of the merged raw text) so that editing a bullet
 * invalidates exactly its own cached row rather than silently mismatching (§16.2 P4).
 *
 * PURE module: no React, no `store`, no DB imports (see README.md). It therefore takes a structurally
 * minimal `JournalEntryForBullets` rather than the real `JournalEntry` class -- the real class is
 * assignable to it, and importing the class here would drag mobx-graphlink into the vitest Node lane.
 *
 * See `Docs/Plans/Others/2026-10-19_EchoHunt.md` §4.1, §4.4, §16.2 P4.
 */

// hashtags / ids
// ==========

/** FNV-1a 32-bit. Local (rather than imported from `Utils/General`) so this module stays
 * dependency-free and so the hash is well-distributed over short bullet texts. */
export function Fnv1a32(text: string): number {
	let hash = 2166136261;
	for (let i = 0; i < text.length; i++) {
		hash ^= text.charCodeAt(i);
		hash = Math.imul(hash, 16777619);
	}
	return hash >>> 0;
}

/** The `sourceTextHash` stored on a cached corpus row. Includes the length so that two texts that
 * collide on the 32-bit hash still produce different values. */
export function HashBulletText(text: string): string {
	return `${Fnv1a32(text).toString(16).padStart(8, "0")}:${text.length}`;
}

// input shapes
// ==========

/** The minimal view of a journal segment this module needs. `JournalSegment` is assignable to it. */
export type JournalSegmentForBullets = {
	id?: string|n;
	/** non-null on "wake marker" segments, which are not dreams and must be skipped (§16.2 P4) */
	wakeTime?: number|n;
	lucid?: boolean|n;
	semiLucid?: boolean|n;
	publicText?: string|n;
};

/** The minimal view of a journal entry this module needs. `JournalEntry` is assignable to it. */
export type JournalEntryForBullets = {
	id: string;
	createdAt?: number|n;
	sleepTime?: number|n;
	wakeTime?: number|n;
	segments: Array<JournalSegmentForBullets|n>;
};

// output shapes
// ==========

export type SourceBullet = {
	/** stable id: `entryId/segmentId/<ordinal of the top-level bullet>` */
	id: string;
	/** `entryId/segmentId` -- the parent dream (§4.1) */
	dreamId: string;
	entryId: string;
	segmentId: string;
	/** the dream's approximate start time (entry sleep-time, else wake-time, else createdAt) */
	date: number|n;
	/** the `### ` section title the bullet sat under; null when it preceded any heading */
	heading: string|n;
	/** the bullet's text verbatim (with any child bullets merged in) */
	rawText: string;
	/** `HashBulletText(rawText)` -- cache-invalidation key (§16.2 P4) */
	sourceTextHash: string;
	/** lucidity hints carried through from the segment (§16.2 P4); the lucid PHASE is judged later */
	lucid: boolean;
	semiLucid: boolean;
	/** how many child bullets were merged into this one */
	childCount: number;
};

/** Non-fatal parsing observations, surfaced in the Corpus tab so a malformed post is visible rather
 * than silently contributing zero bullets. */
export const ExtractWarningKind_values = ["multipleGroups", "noBullets", "emptyPublicText", "duplicateSegmentId"] as const;
export type ExtractWarningKind = typeof ExtractWarningKind_values[number];

export type ExtractWarning = {
	kind: ExtractWarningKind;
	message: string;
	/** where it happened (`entryId/segmentId`, or `entryId` for entry-level problems) */
	dreamId: string;
};

export type ExtractResult = {
	bullets: SourceBullet[];
	warnings: ExtractWarning[];
	/** the `# ` groups found in the post (before truncation to the first one) */
	groupTitles: string[];
};

// parsing
// ==========

const groupHeading_re = /^#(?!#)\s+(.*)$/;
const sectionHeading_re = /^#{2,6}\s+(.*)$/;
const bullet_re = /^([ \t]*)[*+-]\s+(.*)$/;

/** Splits a `publicText` body into its `# ` groups. Text before the first group becomes a group with
 * a null title (so a post that forgot the header still yields bullets). */
export function SplitPublishingGroups(text: string): Array<{title: string|n, body: string[]}> {
	const lines = text.replace(/\r\n?/g, "\n").split("\n");
	const groups: Array<{title: string|n, body: string[]}> = [];
	let current: {title: string|n, body: string[]} = {title: null, body: []};

	for (const line of lines) {
		const groupMatch = groupHeading_re.exec(line);
		if (groupMatch != null) {
			groups.push(current);
			current = {title: groupMatch[1].trim(), body: []};
			continue;
		}
		current.body.push(line);
	}
	groups.push(current);

	// drop a leading "preamble" group that has no bullets of its own, so groupTitles reads naturally
	return groups.filter((group, i)=>i == 0 ? HasAnyBullet(group.body) : true);
}
function HasAnyBullet(lines: string[]): boolean {
	return lines.some(line=>bullet_re.test(line));
}

/** Parses one group's lines into bullets, binding each bullet to the `### ` heading above it. */
export function ParseBulletsFromGroup(lines: string[]): Array<{heading: string|n, rawText: string, childCount: number}> {
	const result: Array<{heading: string|n, rawText: string, childCount: number}> = [];
	let heading: string|n = null;
	/** the entry currently accepting indented child bullets */
	let topLevel: {rawText: string, childCount: number}|n = null;

	for (const line of lines) {
		const sectionMatch = sectionHeading_re.exec(line);
		if (sectionMatch != null) {
			heading = sectionMatch[1].trim() || null;
			topLevel = null; // a heading always closes the previous bullet
			continue;
		}

		const bulletMatch = bullet_re.exec(line);
		if (bulletMatch == null) {
			// prose or blank line: context only. A blank line does NOT close a bullet, because posts
			// commonly separate bullets with blank lines.
			continue;
		}

		const indent = bulletMatch[1];
		const text = bulletMatch[2].trim();
		if (text.length == 0) continue;

		if (indent.length > 0 && topLevel != null) {
			// child bullet -> merged into its parent unit (§4.4)
			topLevel.rawText = `${topLevel.rawText}; ${text}`;
			topLevel.childCount++;
			continue;
		}

		const bullet = {heading, rawText: text, childCount: 0};
		result.push(bullet);
		topLevel = bullet;
	}

	return result;
}

/** Extracts the corpus bullets from one segment's `publicText`. */
export function ExtractBulletsFromText(text: string|n, opts: {
	entryId: string;
	segmentId: string;
	date?: number|n;
	lucid?: boolean|n;
	semiLucid?: boolean|n;
}): ExtractResult {
	const dreamId = `${opts.entryId}/${opts.segmentId}`;
	const warnings: ExtractWarning[] = [];
	const trimmed = (text ?? "").trim();
	if (trimmed.length == 0) {
		warnings.push({kind: "emptyPublicText", dreamId, message: "The segment has no publicText."});
		return {bullets: [], warnings, groupTitles: []};
	}

	const groups = SplitPublishingGroups(trimmed);
	const groupTitles = groups.map(a=>a.title).filter(a=>a != null) as string[];

	// one segment is one dream, so only the first group is extracted (decided 2026-10-19)
	const groupsWithBullets = groups.filter(group=>ParseBulletsFromGroup(group.body).length > 0);
	if (groupsWithBullets.length > 1) {
		warnings.push({
			kind: "multipleGroups", dreamId,
			message: `publicText contains ${groupsWithBullets.length} groups ("${groupsWithBullets.map(a=>a.title ?? "(untitled)").join("\", \"")}"); only the first ("${groupsWithBullets[0].title ?? "(untitled)"}") was extracted. Split the post across segments if these are separate dreams.`,
		});
	}

	const group = groupsWithBullets[0];
	if (group == null) {
		warnings.push({kind: "noBullets", dreamId, message: "publicText contains no `* ` bullets (only prose/headings)."});
		return {bullets: [], warnings, groupTitles};
	}

	const parsed = ParseBulletsFromGroup(group.body);
	const bullets: SourceBullet[] = parsed.map((item, ordinal)=>({
		id: `${dreamId}/${ordinal}`,
		dreamId,
		entryId: opts.entryId,
		segmentId: opts.segmentId,
		date: opts.date ?? null,
		heading: item.heading,
		rawText: item.rawText,
		sourceTextHash: HashBulletText(item.rawText),
		lucid: opts.lucid ?? false,
		semiLucid: opts.semiLucid ?? false,
		childCount: item.childCount,
	}));

	return {bullets, warnings, groupTitles};
}

/** Extracts bullets from every dream segment of an entry, with warnings. */
export function ExtractBulletsDetailed(entry: JournalEntryForBullets): ExtractResult {
	const bullets: SourceBullet[] = [];
	const warnings: ExtractWarning[] = [];
	const groupTitles: string[] = [];

	const date = entry.sleepTime ?? entry.wakeTime ?? entry.createdAt ?? null;
	const seenSegmentIds = new Set<string>();

	entry.segments.filter(a=>a != null).forEach((segment, segmentIndex)=>{
		// wake markers are not dreams (§16.2 P4)
		if (segment.wakeTime != null) return;

		const segmentId = segment.id ?? `${segmentIndex}`;
		if (seenSegmentIds.has(segmentId)) {
			warnings.push({
				kind: "duplicateSegmentId", dreamId: `${entry.id}/${segmentId}`,
				message: `Segment id "${segmentId}" appears more than once in this entry; later occurrences will overwrite each other's cached corpus rows.`,
			});
		}
		seenSegmentIds.add(segmentId);

		const result = ExtractBulletsFromText(segment.publicText, {
			entryId: entry.id,
			segmentId,
			date,
			lucid: segment.lucid,
			semiLucid: segment.semiLucid,
		});
		bullets.push(...result.bullets);
		warnings.push(...result.warnings);
		groupTitles.push(...result.groupTitles);
	});

	return {bullets, warnings, groupTitles};
}

/** `ExtractBullets(entry)` -- the plain §16.2 P4 signature, for callers that only want the bullets.
 * Use `ExtractBulletsDetailed` when you also want the warnings (the Corpus tab does). */
export function ExtractBullets(entry: JournalEntryForBullets): SourceBullet[] {
	return ExtractBulletsDetailed(entry).bullets;
}
